import {
  AlertTriangle,
  BadgeDollarSign,
  RefreshCw,
  Ticket,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatCredits, formatPlanName } from '../../lib/billing-format';
import { Button } from '../ui/button';
import type { WebsiteCredits } from '../../lib/auth';

export function PaymentRequiredBanner({
  message,
  credits,
  loadingCredits,
  onOpenBilling,
  onOpenRedeem,
  onRefreshCredits,
}: {
  message: string;
  credits?: WebsiteCredits | null;
  loadingCredits: boolean;
  onOpenBilling: () => void;
  onOpenRedeem: () => void;
  onRefreshCredits: () => void;
}) {
  const planName = formatPlanName(credits?.plan_code);

  return (
    <div className="mb-3 rounded-xl border border-amber-200/70 bg-amber-50 px-4 py-4 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-amber-500/12 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
          <AlertTriangle className="size-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Payment required</div>
          <p className="mt-1 text-sm leading-6 text-amber-800 dark:text-amber-100/90">
            {message ||
              'This request needs more credits. Recharge or redeem a code, then retry.'}
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <span
              className={cn(
                'inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium',
                'border-amber-300/80 bg-white/70 text-amber-800',
                'dark:border-amber-400/30 dark:bg-black/10 dark:text-amber-100'
              )}
            >
              {loadingCredits
                ? 'Checking balance…'
                : `${formatCredits(credits?.available_credits ?? 0)} credits available`}
            </span>

            {planName ? (
              <span
                className={cn(
                  'inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium',
                  'border-amber-300/80 bg-white/70 text-amber-800',
                  'dark:border-amber-400/30 dark:bg-black/10 dark:text-amber-100'
                )}
              >
                Current plan: {planName}
              </span>
            ) : null}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={onOpenBilling}>
              <BadgeDollarSign className="size-4" />
              Get more credits
            </Button>

            <Button size="sm" variant="outline" onClick={onOpenRedeem}>
              <Ticket className="size-4" />
              Redeem code
            </Button>

            <Button size="sm" variant="ghost" onClick={onRefreshCredits}>
              <RefreshCw className="size-4" />
              Refresh balance
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
